import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import PatientDashboard from './pages/patient/PatientDashboard';
import PatientInvoices from './pages/patient/PatientInvoices';
import InvoiceDetail from './pages/patient/InvoiceDetail';
import PaymentPage from './pages/patient/PaymentPage';
import PaymentHistory from './pages/patient/PaymentHistory';
import NotificationsPage from './pages/patient/NotificationsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminPayments from './pages/admin/AdminPayments';
import AdminTransactions from './pages/admin/AdminTransactions';
import AdminUsers from './pages/admin/AdminUsers';

// Layout
import AppLayout from './components/Layout/AppLayout';

const staffRoles = ['receptionniste', 'medecin', 'caissier', 'admin'];

const getHomeRoute = (role) => {
  if (role === 'patient') return '/patient';
  if (staffRoles.includes(role)) return '/admin';
  return '/login';
};

const PrivateRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-page">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={getHomeRoute(user.role)} />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={getHomeRoute(user.role)} />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

          {/* Patient */}
          <Route path="/patient" element={
            <PrivateRoute roles={['patient']}><AppLayout><PatientDashboard /></AppLayout></PrivateRoute>
          } />
          <Route path="/patient/invoices" element={
            <PrivateRoute roles={['patient']}><AppLayout><PatientInvoices /></AppLayout></PrivateRoute>
          } />
          <Route path="/patient/invoices/:id" element={
            <PrivateRoute roles={['patient']}><AppLayout><InvoiceDetail /></AppLayout></PrivateRoute>
          } />
          <Route path="/patient/pay/:invoiceId" element={
            <PrivateRoute roles={['patient']}><AppLayout><PaymentPage /></AppLayout></PrivateRoute>
          } />
          <Route path="/patient/payments" element={
            <PrivateRoute roles={['patient']}><AppLayout><PaymentHistory /></AppLayout></PrivateRoute>
          } />
          <Route path="/patient/notifications" element={
            <PrivateRoute roles={['patient']}><AppLayout><NotificationsPage /></AppLayout></PrivateRoute>
          } />

          {/* Staff (receptionniste, medecin, caissier, admin) */}
          <Route path="/admin" element={
            <PrivateRoute roles={staffRoles}><AppLayout><AdminDashboard /></AppLayout></PrivateRoute>
          } />
          <Route path="/admin/invoices" element={
            <PrivateRoute roles={['receptionniste', 'medecin', 'admin']}><AppLayout><AdminInvoices /></AppLayout></PrivateRoute>
          } />
          <Route path="/admin/payments" element={
            <PrivateRoute roles={['caissier', 'admin']}><AppLayout><AdminPayments /></AppLayout></PrivateRoute>
          } />
          <Route path="/admin/transactions" element={
            <PrivateRoute roles={['caissier', 'admin']}><AppLayout><AdminTransactions /></AppLayout></PrivateRoute>
          } />
          <Route path="/admin/users" element={
            <PrivateRoute roles={['admin']}><AppLayout><AdminUsers /></AppLayout></PrivateRoute>
          } />

          {/* Redirect */}
          <Route path="/" element={<Navigate to="/login" />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
      <ToastContainer position="top-right" autoClose={4000} />
    </AuthProvider>
  );
}

export default App;
